// A practice session: 10 questions generated on the server, one try each. A progress bar, a streak counter and
// XP (Kids: stars) at the top, with the active topics as removable chips; Check / Next (Enter) at the bottom with
// rule-based feedback in an aria-live region; a summary at the end. प्रवीण is timed for Adults (20 seconds per
// question part), never for Kids. The server grades every answer; the browser only shows what it says and keeps
// guest receipts. Listening questions play a teacher's recording, or the device voice reads a masked text that is
// unmasked only at the moment it plays. With विकल्प सहित (?vikalp=1) a chip says so in the header; a variant cue
// carries the विकल्प badge, and a variant answer is named (with its standard form) only after it is graded.

import type { FC, ReactNode } from 'react'
import type { L } from '@/prakrit/lib/lessons/types'
import type { GradeResponse } from '@/prakrit/components/lesson/lesson-types'
import type {
    AnswerResponse,
    Level,
    PlayedQuestion,
    PracticeAnswer,
    PracticeQuestion,
    QuestionResponse,
    SessionMode,
    SetPlace,
    Unavailable
} from '@/prakrit/components/practice/practice-types'

import Link from 'next/link'
import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react'
import { MotionConfig } from 'motion/react'
import {
    ArrowRight,
    BookOpen,
    CircleAlert,
    Filter,
    Flame,
    LoaderCircle,
    RotateCcw,
    Sparkles,
    Star,
    Timer,
    Turtle,
    Volume2,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePrefs } from '@/lib/client/prefs'
import { useProgress } from '@/prakrit/lib/client/progress'
import { useAudio } from '@/prakrit/lib/client/audio'
import { useSetProgress } from '@/prakrit/lib/client/practice-sets'
import { Bi } from '@/prakrit/components/common/bi'
import { StatusBadge } from '@/prakrit/components/common/status-badge'
import { FeedbackPanel } from '@/prakrit/components/lesson/feedback-panel'
import { SentenceBuilder } from '@/prakrit/components/lesson/sentence-builder'
import { StepPrompt, useMotionSetting } from '@/prakrit/components/lesson/steps/step-ui'
import { ChoiceOptions } from '@/prakrit/components/practice/choice-options'
import { QuestionCueView } from '@/prakrit/components/practice/question-cue'
import { TableQuestion } from '@/prakrit/components/practice/table-question'
import { OrderQuestion } from '@/prakrit/components/practice/order-question'
import { SessionSummary } from '@/prakrit/components/practice/session-summary'
import { PlanNotice } from '@/components/plans/plan-notice'
import { SESSION_LENGTH } from '@/prakrit/components/practice/practice-types'
import { unmaskSpeech } from '@/prakrit/components/practice/listen-mask'

import {
    LEVEL_INFO,
    MIXED_INFO,
    UNAVAILABLE,
    modeInfo,
    sessionName
} from '@/prakrit/components/practice/practice-catalog'
import {
    TOPIC_BY_ID,
    VIKALP_PARAM,
    practiceHubHref,
    topicQuery,
    usesVikalp
} from '@/prakrit/components/practice/practice-topics'

type Phase = 'waiting' | 'loading' | 'ready' | 'checking' | 'answered' | 'done' | 'unavailable' | 'load-error'

type CheckError = 'network' | 'expired' | null

interface DueItem {
    ref: string
    skill: string
}

interface PracticeSessionProps {
    mode: SessionMode
    level: Level
    /** A root or stem from the URL (?root= / ?stem=). */
    focus: string | null
    /** The chosen topics this game understands (?topic=); Today's practice has none. */
    topics: string[]
    /** विकल्प सहित (?vikalp=1), already reduced to the games that read it. */
    vikalp: boolean
    /** A numbered practice set of the game's bank (?set=7); null for a free session. */
    set?: number | null
}

/** The same game and level with other topics or another विकल्प choice (removing a chip starts a new session). */
const sessionHref = (mode: SessionMode, level: Level, focus: string | null, topics: string[], vikalp: boolean): string => {
    const params = new URLSearchParams({ level })

    if (focus) params.set('focus', focus)
    if (topics.length > 0 && mode !== 'aaj') params.set('topic', topicQuery(topics))
    if (vikalp && usesVikalp(mode)) params.set(VIKALP_PARAM, '1')

    return `/practice/${mode}?${params.toString().replace(/%2C/g, ',')}`
}

const LABEL: Record<'check' | 'checkAgain' | 'checking' | 'next' | 'results' | 'newQuestion', L> = {
    check: { hi: 'जाँचें', en: 'Check' },
    checkAgain: { hi: 'फिर से जाँचें', en: 'Check again' },
    checking: { hi: 'जाँच रहे हैं…', en: 'Checking…' },
    next: { hi: 'अगला प्रश्न', en: 'Next question' },
    results: { hi: 'परिणाम देखें', en: 'See the results' },
    newQuestion: { hi: 'नया प्रश्न', en: 'New question' }
}

const HINT: Record<PracticeQuestion['kind'], L> = {
    choice: { hi: 'उत्तर चुनें (1–4 या तीर), फिर Enter दबाएँ', en: 'Choose an answer (1–4 or the arrows), then press Enter' },
    table: { hi: 'हर खाली खाने के लिए एक रूप चुनें', en: 'Choose a form for every empty cell' },
    build: { hi: 'शब्दों को छूकर या Enter से वाक्य में रखें; ← → से रखे शब्द की जगह बदलें', en: 'Tap the words, or press Enter on them, to build the sentence; ← → moves a placed word' },
    order: { hi: 'पंक्तियों को क्रम से चुनें; Alt + ↑ / ↓ से जगह बदलें', en: 'Choose the lines in order; Alt + ↑ / ↓ moves them' }
}

const answerReady = (q: PracticeQuestion | null, a: PracticeAnswer): boolean => {
    if (!q) return false

    switch (q.kind) {
        case 'choice': return Boolean(a.id)
        case 'table': return q.table.slots.every(s => Boolean(a.slots?.[s.id]))
        case 'build': return (a.tiles?.length ?? 0) > 0
        case 'order': return (a.order?.length ?? 0) === q.lines.length
    }
}

/** The lesson player's feedback panel reads the lesson grade shape; a practice answer carries the same fields. */
const asGrade = (r: AnswerResponse): GradeResponse => ({
    correct: r.correct,
    feedback: r.feedback,
    reason: r.reason,
    note: r.note,
    expected: r.expected,
    code: r.code,
    ref: r.ref,
    skill: r.skill,
    label: r.label,
    xpAwarded: r.xpAwarded,
    receipt: r.receipt,
    tries: 1,
    firstTry: r.correct,
    repeat: Boolean(r.repeat),
    attempt: r.attempt
})

const isPrakritText = (text: string): boolean => /[ऀ-ॿ]/.test(text) && !text.includes(' · ')

/** The right answer of a wrong answer as plain text, for screen readers (the page shows it beside the question). */
const spokenAnswer = (q: PracticeQuestion, r: AnswerResponse): string | null => {
    if (r.correct) return null

    if (q.kind === 'table') {
        const slots = q.table.slots
        const missed = (r.slotResults ?? []).filter(s => !s.correct)
        const text = missed.map(s => {
            const slot = slots.find(x => x.id === s.id)

            return slot ? `${slot.label.hi} (${slot.label.en}): ${s.expected}` : s.expected
        }).join('; ')

        return text || null
    }

    if (q.kind === 'order') {
        const all = q.lines
        const lines = (r.expectedOrder ?? []).map(id => all.find(l => l.id === id)?.pra).filter(Boolean)

        return lines.length > 0 ? lines.map((line, i) => `${i + 1}. ${line}`).join(' ') : null
    }

    return r.expected ?? null
}

/** Plays the teacher's recording that came with a listening question (nothing is synthesised). */
const ListenControls: FC<{ audio: { normal?: string, slow?: string }, kids: boolean }> = ({ audio, kids }): ReactNode => {
    const current = useRef<HTMLAudioElement | null>(null)
    const [playing, setPlaying] = useState<'normal' | 'slow' | null>(null)
    const [failed, setFailed] = useState(false)

    useEffect(() => () => {
        current.current?.pause()
        current.current = null
    }, [])

    const play = (speed: 'normal' | 'slow') => {
        const url = speed === 'slow' ? audio.slow ?? audio.normal : audio.normal ?? audio.slow

        if (!url) return

        current.current?.pause()

        const element = new Audio(url)

        // Without a separate slow recording, the real recording is played more slowly.
        if (speed === 'slow' && !audio.slow) element.playbackRate = 0.75

        element.onended = () => setPlaying(null)
        element.onerror = () => {
            setPlaying(null)
            setFailed(true)
        }
        current.current = element
        setFailed(false)
        setPlaying(speed)
        element.play().catch(() => {
            setPlaying(null)
            setFailed(true)
        })
    }

    return (
        <div className='flex flex-col items-center gap-3 rounded-2xl border border-border bg-card px-4 py-6 text-center shadow-card'>
            <PlayButtons playing={playing} onPlay={play} kids={kids} />
            {failed && (
                <p role='alert' className='text-sm text-danger'>
                    <Bi hi='रिकॉर्डिंग चल नहीं सकी। फिर से दबाइए।' en='The recording could not play. Please press again.' />
                </p>
            )}
        </div>
    )
}

/** The big listen button and the slow button, shared by recordings and the device voice. */
const PlayButtons: FC<{ playing: 'normal' | 'slow' | null, onPlay: (speed: 'normal' | 'slow') => void, kids: boolean }> = ({ playing, onPlay, kids }): ReactNode => (
    <div className='flex flex-wrap items-center justify-center gap-3'>
        <button
            type='button'
            onClick={() => onPlay('normal')}
            aria-pressed={playing === 'normal'}
            className={cn(
                'flex items-center justify-center rounded-full bg-saffron text-foreground shadow-card transition-transform hover:scale-105 motion-reduce:transition-none motion-reduce:hover:scale-100',
                kids ? 'size-24' : 'size-20',
                playing === 'normal' && 'ring-4 ring-saffron/30'
            )}
        >
            <Volume2 className={kids ? 'size-11' : 'size-9'} aria-hidden='true' />
            <span className='sr-only'>सुनें · Listen</span>
        </button>
        <button
            type='button'
            onClick={() => onPlay('slow')}
            aria-pressed={playing === 'slow'}
            className='inline-flex min-h-tap items-center gap-2 rounded-full border border-border bg-card px-4 text-sm font-medium hover:bg-secondary'
        >
            <Turtle className='size-4' aria-hidden='true' />
            <Bi hi='धीरे' en='Slowly' layout='inline' always />
        </button>
    </div>
)

/**
 * The device voice reads the question's text. The text arrives masked and is unmasked only inside the click
 * handler, so it never sits in the page as words a learner could read before answering.
 */
const VoiceListenControls: FC<{ say: string, kids: boolean }> = ({ say, kids }): ReactNode => {
    const { speak, stop, playing, voiceSupported, voiceEnabled, setVoiceEnabled } = useAudio()
    const [active, setActive] = useState<'normal' | 'slow' | null>(null)
    const [failed, setFailed] = useState(false)

    useEffect(() => () => stop(), [stop])

    useEffect(() => {
        if (playing === null) setActive(null)
    }, [playing])

    const play = (speed: 'normal' | 'slow') => {
        const text = unmaskSpeech(say)

        if (!text) {
            setFailed(true)

            return
        }

        setFailed(false)
        setActive(speed)
        speak(text, speed)
            .then(ok => {
                if (!ok) {
                    setActive(null)
                    setFailed(true)
                }
            })
            .catch(() => {
                setActive(null)
                setFailed(true)
            })
    }

    if (!voiceSupported || !voiceEnabled) {
        return (
            <div className='flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border bg-card px-4 py-6 text-center'>
                <p className='max-w-md text-sm'>
                    {voiceSupported ? (
                        <Bi hi='इस प्रश्न को डिवाइस की आवाज़ पढ़ती है, जो अभी बन्द है।' en='This question is read by the device voice, which is switched off.' />
                    ) : (
                        <Bi hi='इस ब्राउज़र में हिन्दी आवाज़ नहीं मिली, इसलिए यह प्रश्न सुनाया नहीं जा सकता। दूसरे ब्राउज़र या डिवाइस में आज़माइए।' en='This browser has no Hindi voice, so this question cannot be read aloud. Try another browser or device.' />
                    )}
                </p>
                {voiceSupported && (
                    <button
                        type='button'
                        onClick={() => setVoiceEnabled(true)}
                        className='inline-flex min-h-tap items-center gap-2 rounded-full border border-border bg-background px-5 font-medium hover:border-saffron/60 hover:bg-saffron/5'
                    >
                        <Volume2 className='size-4 text-saffron-strong' aria-hidden='true' />
                        <Bi hi='डिवाइस की आवाज़ चालू करें' en='Turn the device voice on' layout='inline' always />
                    </button>
                )}
            </div>
        )
    }

    return (
        <div className='flex flex-col items-center gap-3 rounded-2xl border border-border bg-card px-4 py-6 text-center shadow-card'>
            <PlayButtons playing={playing ? active : null} onPlay={play} kids={kids} />
            <p className='text-xs text-muted-foreground'>
                <Bi hi='डिवाइस की आवाज़ · उच्चारण अनुमानित है' en='Device voice · the pronunciation is approximate' layout='inline' />
            </p>
            {failed && (
                <p role='alert' className='text-sm text-danger'>
                    <Bi hi='आवाज़ चल नहीं सकी। फिर से दबाइए।' en='The voice could not play. Please press again.' />
                </p>
            )}
        </div>
    )
}

export const PracticeSession: FC<PracticeSessionProps> = ({ mode, level, focus, topics, vikalp, set = null }): ReactNode => {
    const { mode: viewMode, hydrated } = usePrefs()
    const motionSetting = useMotionSetting()
    const { ready, snapshot, review, apply, signedIn } = useProgress()
    const { voiceSupported, voiceEnabled, setVoiceEnabled } = useAudio()
    const kids = viewMode === 'kids'
    const canSpeak = voiceSupported && voiceEnabled
    // श्रवण waits a moment for the browser's voice list (it often arrives late) before asking the first question.
    const [voiceWaited, setVoiceWaited] = useState(mode !== 'shravan')
    const audioSettled = mode !== 'shravan' || canSpeak || voiceWaited
    const startedRun = useRef<number | null>(null)
    const [phase, setPhase] = useState<Phase>('waiting')
    const [question, setQuestion] = useState<PracticeQuestion | null>(null)
    const [answer, setAnswer] = useState<PracticeAnswer>({})
    const [result, setResult] = useState<AnswerResponse | null>(null)
    const [checkError, setCheckError] = useState<CheckError>(null)
    const [played, setPlayed] = useState<PlayedQuestion[]>([])
    const [streak, setStreak] = useState(0)
    const [best, setBest] = useState(0)
    const [unavailable, setUnavailable] = useState<Unavailable | null>(null)
    const [focusMissed, setFocusMissed] = useState(false)
    const [nothingDue, setNothingDue] = useState(false)
    const [left, setLeft] = useState<number | null>(null)
    const [run, setRun] = useState(0)
    // Questions in this session: 10, or the size of the practice set (the last set can be shorter).
    const [length, setLength] = useState(SESSION_LENGTH)
    const [place, setPlace] = useState<SetPlace | null>(null)
    const orderRef = useRef(0)
    const recordedRun = useRef<number | null>(null)
    const { record: recordSet } = useSetProgress(place?.key ?? null)
    const rootRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const dueRef = useRef<DueItem[]>([])
    const seenRef = useRef<string[]>([])
    const startedAt = useRef(0)
    const timeoutFired = useRef(false)
    const streakRef = useRef(0)
    const phaseRef = useRef<Phase>('waiting')
    const answerRef = useRef<PracticeAnswer>({})
    const checkRef = useRef<((timeout?: boolean) => void) | null>(null)
    const primaryRef = useRef<(() => void) | null>(null)

    phaseRef.current = phase
    answerRef.current = answer

    const load = useCallback(async (index: number) => {
        setPhase('loading')
        setResult(null)
        setAnswer({})
        setCheckError(null)
        setLeft(null)
        timeoutFired.current = false

        const params = new URLSearchParams({ mode, level, view: kids ? 'kids' : 'adults' })

        if (seenRef.current.length > 0) params.set('seen', seenRef.current.join(','))
        if (set !== null) {
            params.set('set', String(set))
            params.set('pos', String(index))
            params.set('order', String(orderRef.current))
        }
        if (mode !== 'aaj' && topics.length > 0) params.set('topic', topicQuery(topics))
        if (vikalp) params.set(VIKALP_PARAM, '1')
        if (canSpeak) params.set('voice', '1')

        if (mode === 'aaj') {
            const due = dueRef.current[index]

            if (due) {
                params.set('focus', due.ref)

                if (due.skill) params.set('skill', due.skill)
            }
        }
        else if (focus) {
            params.set('focus', focus)
        }

        try {
            const res = await fetch(`/api/practice/question?${params.toString()}`, { cache: 'no-store' })

            if (!res.ok) throw new Error(`HTTP ${res.status}`)

            const data = await res.json() as QuestionResponse

            if ('unavailable' in data) {
                if (index === 0) {
                    setUnavailable(data.unavailable)
                    setPhase('unavailable')
                }
                else {
                    setPhase('done')
                }

                return
            }

            seenRef.current = [...seenRef.current, data.question.item]

            if (data.question.set) {
                setLength(data.question.set.size)
                setPlace(data.question.set)
            }

            if (index === 0 && focus && mode !== 'aaj' && !data.question.focusUsed) setFocusMissed(true)

            setQuestion(data.question)
            startedAt.current = Date.now()
            setPhase('ready')
            window.requestAnimationFrame(() => contentRef.current?.scrollTo({ top: 0 }))
        }
        catch {
            setPhase('load-error')
        }
    }, [mode, level, kids, focus, topics, vikalp, canSpeak, set])

    useEffect(() => {
        if (mode !== 'shravan') return

        const id = window.setTimeout(() => setVoiceWaited(true), 1500)

        return () => window.clearTimeout(id)
    }, [mode])

    // Start (and restart) once the device preferences and progress are known (and, for श्रवण, the device voice).
    useEffect(() => {
        if (!hydrated || !ready || !audioSettled) return
        if (startedRun.current === run) return

        startedRun.current = run

        if (mode === 'aaj') {
            const now = Date.now()
            const due = snapshot.mastery
                .filter(m => m.dueAt && Date.parse(m.dueAt) <= now)
                .sort((a, b) => Date.parse(a.dueAt as string) - Date.parse(b.dueAt as string))
                .map(m => ({ ref: m.ref, skill: m.skill }))
            const listed = review.map(r => ({ ref: r.ref, skill: '' })).filter(r => !due.some(d => d.ref === r.ref))

            dueRef.current = [...due, ...listed].slice(0, SESSION_LENGTH)
            setNothingDue(dueRef.current.length === 0)
        }

        seenRef.current = []
        streakRef.current = 0
        // A new random order for the set's questions on every play.
        orderRef.current = Math.floor(Math.random() * 2147483646) + 1
        setPlayed([])
        setStreak(0)
        setBest(0)
        setUnavailable(null)
        setFocusMissed(false)
        load(0)
        // The session starts once per run; later progress updates must not restart it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hydrated, ready, run, audioSettled])

    // The device voice turned up late (or was just switched on): try श्रवण again with it.
    useEffect(() => {
        if (canSpeak && phase === 'unavailable' && unavailable === 'no_recordings') setRun(r => r + 1)
    }, [canSpeak, phase, unavailable])

    const check = useCallback(async (timeout = false) => {
        if (!question || phaseRef.current !== 'ready') return

        // Block a second submit before the next render (double click, Enter + click).
        phaseRef.current = 'checking'
        setPhase('checking')
        setCheckError(null)

        try {
            const res = await fetch('/api/practice/answer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: question.token, answer: timeout ? { ...answerRef.current, timeout: true } : answerRef.current })
            })

            if (res.status === 400 || res.status === 410) {
                setCheckError('expired')
                setPhase('ready')

                return
            }

            if (!res.ok) throw new Error(`HTTP ${res.status}`)

            const data = await res.json() as AnswerResponse
            const nextStreak = data.correct ? streakRef.current + 1 : 0

            streakRef.current = nextStreak
            setStreak(nextStreak)
            setBest(b => Math.max(b, nextStreak))
            setResult(data)
            setLeft(null)
            setPlayed(list => [...list, { index: list.length, question, result: data }])
            setPhase('answered')
            apply({ receipts: data.receipt ? [data.receipt] : [], attempts: data.attempts })
        }
        catch {
            setCheckError('network')
            setPhase('ready')
        }
    }, [question, apply])

    checkRef.current = check

    // The प्रवीण timer (Adults): counts from when the question appeared; at zero the answer is sent as timed out.
    useEffect(() => {
        if (phase !== 'ready' || !question?.timed) return

        const total = question.seconds * 1000

        const tick = () => {
            const remaining = Math.max(0, total - (Date.now() - startedAt.current))

            setLeft(Math.ceil(remaining / 1000))

            if (remaining <= 0 && !timeoutFired.current) {
                timeoutFired.current = true
                checkRef.current?.(true)
            }
        }

        tick()

        const id = window.setInterval(tick, 250)

        return () => window.clearInterval(id)
    }, [phase, question])

    const next = useCallback(() => {
        if (played.length >= length) setPhase('done')
        else load(played.length)
    }, [played.length, length, load])

    const restart = () => setRun(r => r + 1)

    // A finished practice set: keep the learner's score for the list of sets.
    useEffect(() => {
        if (phase !== 'done' || !place || recordedRun.current === run) return

        recordedRun.current = run
        recordSet(place.set, played.filter(p => p.result.correct).length, Math.min(played.length, place.size))
    }, [phase, place, run, played, recordSet])

    // ------------------------------------------------------------ primary action and keys

    const locked = phase !== 'ready'
    const settled = phase === 'answered'
    const canCheck = phase === 'ready' && answerReady(question, answer)
    const last = played.length >= length
    let primary: { label: L, action: (() => void) | null, busy?: boolean, tone: 'default' | 'success' } | null = null

    if (phase === 'ready' && checkError === 'expired') primary = { label: LABEL.newQuestion, action: () => load(played.length), tone: 'default' }
    else if (phase === 'ready') primary = { label: checkError ? LABEL.checkAgain : LABEL.check, action: canCheck || timeoutFired.current ? () => check(timeoutFired.current) : null, tone: 'default' }
    else if (phase === 'checking') primary = { label: LABEL.checking, action: null, busy: true, tone: 'default' }
    else if (phase === 'answered') primary = { label: last ? LABEL.results : LABEL.next, action: next, tone: result?.correct ? 'success' : 'default' }

    primaryRef.current = primary?.action ?? null

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.altKey || event.ctrlKey || event.metaKey || event.isComposing || event.repeat) return

            const root = rootRef.current
            const target = event.target instanceof HTMLElement ? event.target : null

            if (!root || !target) return
            if (!root.contains(target) && target !== document.body) return

            // Number keys choose an option of a choice question.
            if (/^[1-9]$/.test(event.key) && phaseRef.current === 'ready') {
                const options = root.querySelectorAll<HTMLElement>('[data-choice-group] [data-option]')
                const option = options[Number(event.key) - 1]

                if (option && !target.closest('input, textarea, select, [contenteditable="true"]')) {
                    event.preventDefault()
                    option.click()
                    option.focus()
                }

                return
            }

            if (event.key !== 'Enter' || event.shiftKey) return

            const choice = target.closest<HTMLElement>('[data-enter-submit]')

            if (choice) {
                if (choice.getAttribute('aria-checked') !== 'true') return

                event.preventDefault()
                primaryRef.current?.()

                return
            }

            if (target.closest('button, a, input, textarea, select, summary, [role="button"], [contenteditable="true"]')) return

            event.preventDefault()
            primaryRef.current?.()
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [])

    // ------------------------------------------------------------ rendering

    const name = sessionName(mode)
    const answered = played.length
    const percent = Math.round((100 * Math.min(answered, length)) / length)
    const correctCount = played.filter(p => p.result.correct).length
    const xp = played.reduce((sum, p) => sum + p.result.xpAwarded, 0)
    const tone = checkError === 'network' ? 'error' : result ? (result.correct ? 'correct' : 'wrong') : null
    const qIndex = answered + (settled ? 0 : 1)
    const answerForReaders = result && question ? spokenAnswer(question, result) : null

    const renderQuestion = (q: PracticeQuestion): ReactNode => {
        switch (q.kind) {
            case 'choice':
                return (
                    <div data-choice-group>
                        <ChoiceOptions
                            options={q.options}
                            value={answer.id ?? null}
                            onChange={id => setAnswer({ id })}
                            locked={locked}
                            result={result}
                            kids={kids}
                            label={`${q.prompt.hi} · ${q.prompt.en}`}
                        />
                    </div>
                )
            case 'table':
                return (
                    <TableQuestion
                        table={q.table}
                        value={answer.slots ?? {}}
                        onChange={(slot, option) => setAnswer(a => ({ slots: { ...(a.slots ?? {}), [slot]: option } }))}
                        locked={locked}
                        result={result}
                        kids={kids}
                    />
                )
            case 'build':
                return (
                    <div className='flex flex-col gap-5'>
                        <div className='rounded-2xl border border-border bg-card px-4 py-5 text-center shadow-card'>
                            <p className='mb-1 text-xs font-semibold text-muted-foreground'>
                                <Bi hi='अर्थ' en='Meaning' layout='inline' className='justify-center' always />
                            </p>
                            <p className='flex flex-col items-center gap-1'>
                                <span lang='hi' data-ex='hi' className={cn('hindi font-semibold', kids ? 'text-3xl' : 'text-2xl')}>{q.target.hi}</span>
                                <span lang='en' data-ex='en' className='text-lg text-muted-foreground'>{q.target.en}</span>
                            </p>
                        </div>
                        <SentenceBuilder
                            tiles={q.tiles}
                            slots={q.slots}
                            value={answer.tiles ?? []}
                            onChange={tiles => setAnswer({ tiles })}
                            disabled={locked}
                            status={result ? (result.correct ? 'correct' : 'wrong') : null}
                            uid={`practice-${run}-${answered}`}
                        />
                    </div>
                )
            case 'order':
                return (
                    <OrderQuestion
                        story={q.story}
                        lines={q.lines}
                        showMeaning={q.showMeaning}
                        value={answer.order ?? []}
                        onChange={order => setAnswer({ order })}
                        locked={locked}
                        result={result}
                    />
                )
        }
    }

    const hubHref = practiceHubHref(mode !== 'aaj' ? topics : [], vikalp)
    const showTopics = topics.length > 0 && mode !== 'aaj'

    const closeLink = (
        <Link
            href={hubHref}
            aria-label='अभ्यास बन्द करें, सभी खेलों पर लौटें · Close and go back to all games'
            className='inline-flex size-11 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground'
        >
            <X className='size-5' aria-hidden='true' />
        </Link>
    )

    if (!hydrated) {
        return (
            <div className='flex min-h-[60vh] items-center justify-center' role='status'>
                <LoaderCircle className='size-8 animate-spin text-saffron-strong' aria-hidden='true' />
                <span className='sr-only'>लोड हो रहा है · Loading</span>
            </div>
        )
    }

    const timerShare = question?.timed && left !== null ? Math.max(0, Math.min(1, left / question.seconds)) : null

    return (
        <MotionConfig reducedMotion={motionSetting}>
            <div ref={rootRef} className='paper fixed inset-0 z-[45] flex flex-col bg-background'>
                <header className='border-b border-border bg-background/95 backdrop-blur'>
                    <div className='mx-auto flex w-full max-w-3xl items-center gap-2 px-2 py-2 sm:gap-3 sm:px-4'>
                        {closeLink}
                        <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
                            <p className='truncate py-0.5 text-sm font-semibold leading-normal'>
                                <span lang='hi' className='hindi'>{name.hi}</span>
                                <span aria-hidden='true' className='px-1 text-muted-foreground/70'>·</span>
                                <span lang='en' className='font-normal text-muted-foreground'>{name.en}</span>
                                <span aria-hidden='true' className='px-1 text-muted-foreground/70'>·</span>
                                <span lang='hi' className='hindi text-saffron-strong'>{LEVEL_INFO[level].name.hi}</span>
                            </p>
                            <div
                                role='progressbar'
                                aria-label='सत्र की प्रगति · Session progress'
                                aria-valuemin={0}
                                aria-valuemax={length}
                                aria-valuenow={Math.min(answered, length)}
                                aria-valuetext={`${Math.min(answered, length)} / ${length}`}
                                className='h-2 w-full overflow-hidden rounded-full bg-secondary'
                            >
                                <div className='h-full rounded-full bg-saffron transition-[width] duration-300 ease-out' style={{ width: `${percent}%` }} />
                            </div>
                        </div>

                        <span
                            className={cn(
                                'inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-sm font-semibold tabular-nums',
                                streak > 0 ? 'bg-terracotta/12 text-terracotta' : 'bg-secondary text-muted-foreground'
                            )}
                            title='लगातार सही · Streak'
                        >
                            <Flame className={cn('size-4', streak >= 3 && 'fill-terracotta/30')} aria-hidden='true' />
                            <span aria-hidden='true'>{streak}</span>
                            <span className='sr-only'>लगातार {streak} सही · streak {streak}</span>
                        </span>

                        {kids ? (
                            <span className='inline-flex shrink-0 items-center gap-1 rounded-full bg-saffron/12 px-2.5 py-1 text-sm font-semibold tabular-nums'>
                                <Star className='size-4 fill-saffron text-saffron-strong' aria-hidden='true' />
                                <span aria-hidden='true'>{correctCount}</span>
                                <span className='sr-only'>{correctCount} सितारे · stars</span>
                            </span>
                        ) : (
                            <span className='inline-flex shrink-0 items-center gap-1 rounded-full bg-saffron/12 px-2.5 py-1 text-sm font-semibold tabular-nums'>
                                <Sparkles className='size-4 text-saffron-strong' aria-hidden='true' />
                                <span aria-hidden='true'>{xp}</span>
                                <span aria-hidden='true' className='text-xs font-medium text-muted-foreground'>XP</span>
                                <span className='sr-only'>{xp} XP</span>
                            </span>
                        )}
                    </div>

                    {timerShare !== null && phase === 'ready' && (
                        <div className='mx-auto flex w-full max-w-3xl items-center gap-2 px-4 pb-2'>
                            <Timer className={cn('size-4 shrink-0', (left ?? 0) <= 5 ? 'text-danger' : 'text-muted-foreground')} aria-hidden='true' />
                            <div className='h-1.5 flex-1 overflow-hidden rounded-full bg-secondary' aria-hidden='true'>
                                <div
                                    className={cn('h-full rounded-full transition-[width] duration-200 ease-linear', (left ?? 0) <= 5 ? 'bg-danger' : 'bg-terracotta')}
                                    style={{ width: `${Math.round(timerShare * 100)}%` }}
                                />
                            </div>
                            <span role='timer' aria-live='off' className={cn('w-12 shrink-0 text-right text-sm font-semibold tabular-nums', (left ?? 0) <= 5 && 'text-danger')}>
                                {left}s
                            </span>
                            <span className='sr-only' aria-live='polite'>
                                {left === 10 ? '10 सेकंड बाक़ी · 10 seconds left' : left === 5 ? '5 सेकंड बाक़ी · 5 seconds left' : ''}
                            </span>
                        </div>
                    )}

                    {(showTopics || vikalp) && (
                        <div className='mx-auto flex w-full max-w-3xl flex-wrap items-center gap-1.5 px-4 pb-2'>
                            {showTopics && (
                                <>
                                    <span className='inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground'>
                                        <Filter className='size-3.5' aria-hidden='true' />
                                        <Bi hi='विषय' en='Topics' layout='inline' always />
                                    </span>
                                    <ul className='flex flex-wrap items-center gap-1.5' aria-label='चुने गए विषय · Chosen topics'>
                                        {topics.map(id => {
                                            const topic = TOPIC_BY_ID[id]

                                            if (!topic) return null

                                            return (
                                                <li key={id} className='inline-flex items-center gap-1 rounded-full border border-saffron/50 bg-saffron/10 py-0.5 pl-2.5 pr-0.5 text-xs font-medium'>
                                                    <span lang='hi' className='hindi'>{topic.full.hi}</span>
                                                    <span aria-hidden='true' className='text-muted-foreground/70'>·</span>
                                                    <span lang='en' className='text-muted-foreground'>{topic.label.en}</span>
                                                    <Link
                                                        href={sessionHref(mode, level, focus, topics.filter(x => x !== id), vikalp)}
                                                        aria-label={`विषय हटाएँ: ${topic.full.hi} (नया सत्र शुरू होगा) · Remove the topic ${topic.full.en} (starts a new session)`}
                                                        className='inline-flex size-8 items-center justify-center rounded-full text-muted-foreground hover:bg-saffron/20 hover:text-foreground'
                                                    >
                                                        <X className='size-3.5' aria-hidden='true' />
                                                    </Link>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                    {topics.length > 1 && (
                                        <Link
                                            href={sessionHref(mode, level, focus, [], vikalp)}
                                            className='inline-flex min-h-8 items-center rounded-full px-2 text-xs font-medium text-saffron-strong underline-offset-4 hover:underline'
                                        >
                                            <Bi hi='सब हटाएँ' en='Clear all' layout='inline' always />
                                        </Link>
                                    )}
                                </>
                            )}
                            {vikalp && (
                                <span className='inline-flex items-center gap-1.5 rounded-full border border-terracotta/40 bg-terracotta/5 py-0.5 pl-1 pr-0.5 text-xs font-medium'>
                                    <StatusBadge kind='alt' withEnglish={false} />
                                    <Bi hi='रूप भी आएँगे' en='variants included' layout='inline' enClassName='text-muted-foreground' always />
                                    <Link
                                        href={sessionHref(mode, level, focus, topics, false)}
                                        aria-label='विकल्प रूप हटाएँ (नया सत्र शुरू होगा) · Exclude variant forms (starts a new session)'
                                        className='inline-flex size-8 items-center justify-center rounded-full text-muted-foreground hover:bg-terracotta/15 hover:text-foreground'
                                    >
                                        <X className='size-3.5' aria-hidden='true' />
                                    </Link>
                                </span>
                            )}
                        </div>
                    )}
                </header>

                <div ref={contentRef} className='flex-1 overflow-y-auto overscroll-contain'>
                    <div className='mx-auto w-full max-w-2xl px-4 pb-10 pt-5 sm:pt-8'>
                        {(phase === 'waiting' || phase === 'loading') && (
                            <div className='flex flex-col items-center gap-3 py-16 text-center' role='status'>
                                <LoaderCircle className='size-8 animate-spin text-saffron-strong' aria-hidden='true' />
                                <Bi hi={answered === 0 ? 'अभ्यास तैयार हो रहा है…' : 'अगला प्रश्न आ रहा है…'} en={answered === 0 ? 'Getting your practice ready…' : 'The next question is coming…'} />
                            </div>
                        )}

                        {phase === 'load-error' && (
                            <div className='flex flex-col items-center gap-4 py-12 text-center' role='alert'>
                                <CircleAlert className='size-8 text-danger' aria-hidden='true' />
                                <p className='max-w-md'>
                                    <Bi hi='प्रश्न लोड नहीं हो सका। इंटरनेट जाँचकर फिर से प्रयास करें।' en='The question could not load. Check your connection and try again.' />
                                </p>
                                <button
                                    type='button'
                                    onClick={() => load(answered)}
                                    className='inline-flex min-h-tap items-center gap-2 rounded-full bg-primary px-6 py-2.5 font-semibold text-primary-foreground shadow-card hover:bg-primary/85'
                                >
                                    <RotateCcw className='size-4' aria-hidden='true' />
                                    <Bi hi='फिर से प्रयास करें' en='Try again' layout='inline' always />
                                </button>
                            </div>
                        )}

                        {phase === 'unavailable' && unavailable === 'needs_plan' && (
                            <PlanNotice kind='set' compact onTrialStarted={restart} className='my-6' />
                        )}

                        {phase === 'unavailable' && unavailable && unavailable !== 'needs_plan' && (
                            <div className='flex flex-col items-center gap-4 py-10 text-center' role='status'>
                                <span className='flex size-16 items-center justify-center rounded-full bg-saffron/12' aria-hidden='true'>
                                    <BookOpen className='size-8 text-saffron-strong' />
                                </span>
                                <h2 className='flex flex-col gap-1'>
                                    <span lang='hi' className='text-xl font-bold'>{UNAVAILABLE[unavailable].hi}</span>
                                    <span lang='en' className='font-sans text-base font-medium text-muted-foreground'>{UNAVAILABLE[unavailable].en}</span>
                                </h2>
                                <p className='max-w-md text-sm'>
                                    {unavailable === 'no_topic' ? (
                                        <Bi
                                            hi={`चुने गए विषयों (${topics.map(id => TOPIC_BY_ID[id]?.full.hi ?? id).join(', ')}) के लिए इस स्तर पर अभी कोई प्रश्न नहीं बन सका। विषय हटाइए, या दूसरा स्तर अथवा खेल चुनिए।`}
                                            en={`No question can be made yet for the chosen topics (${topics.map(id => TOPIC_BY_ID[id]?.full.en ?? id).join(', ')}) at this level. Clear the filter, or choose another level or game.`}
                                        />
                                    ) : unavailable === 'no_recordings' ? (
                                        voiceSupported && !voiceEnabled ? (
                                            <Bi
                                                hi='श्रवण अभ्यास शिक्षक की रिकॉर्डिंग से, या डिवाइस की आवाज़ से चलता है। अभी रिकॉर्डिंग नहीं हैं और डिवाइस की आवाज़ बन्द है।'
                                                en="Listening practice plays a teacher's recording, or your device's voice. There are no recordings yet, and the device voice is switched off."
                                            />
                                        ) : (
                                            <Bi
                                                hi='श्रवण अभ्यास शिक्षक की रिकॉर्डिंग से, या डिवाइस की हिन्दी आवाज़ से चलता है। अभी रिकॉर्डिंग नहीं हैं, और इस ब्राउज़र में हिन्दी आवाज़ नहीं मिली। दूसरे ब्राउज़र या डिवाइस में आज़माइए।'
                                                en="Listening practice plays a teacher's recording, or a Hindi voice on your device. There are no recordings yet, and this browser has no Hindi voice. Try another browser or device."
                                            />
                                        )
                                    ) : unavailable === 'no_stories' ? (
                                        <Bi
                                            hi='पहली कथा तैयार होते ही यह खेल खुल जाएगा।'
                                            en='This game opens as soon as the first story is ready.'
                                        />
                                    ) : (
                                        <Bi
                                            hi='यह अभ्यास जल्द खुलेगा।'
                                            en='This practice opens soon.'
                                        />
                                    )}
                                </p>
                                <div className='flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-center'>
                                    {unavailable === 'no_topic' && (
                                        <Link
                                            href={sessionHref(mode, level, focus, [], vikalp)}
                                            className='inline-flex min-h-tap items-center justify-center gap-2 rounded-full bg-primary px-6 py-2.5 font-semibold text-primary-foreground shadow-card hover:bg-primary/85'
                                        >
                                            <X className='size-4' aria-hidden='true' />
                                            <Bi hi='विषय हटाएँ' en='Clear the filter' layout='inline' always />
                                        </Link>
                                    )}
                                    {unavailable === 'no_recordings' && voiceSupported && !voiceEnabled && (
                                        <button
                                            type='button'
                                            onClick={() => setVoiceEnabled(true)}
                                            className='inline-flex min-h-tap items-center justify-center gap-2 rounded-full bg-primary px-6 py-2.5 font-semibold text-primary-foreground shadow-card hover:bg-primary/85'
                                        >
                                            <Volume2 className='size-4' aria-hidden='true' />
                                            <Bi hi='डिवाइस की आवाज़ चालू करें' en='Turn the device voice on' layout='inline' always />
                                        </button>
                                    )}
                                    <Link
                                        href={hubHref}
                                        className={cn(
                                            'inline-flex min-h-tap items-center justify-center gap-2 rounded-full px-6 py-2.5 font-semibold',
                                            unavailable === 'no_topic' || (unavailable === 'no_recordings' && voiceSupported && !voiceEnabled)
                                                ? 'border border-border bg-card hover:border-saffron/60 hover:bg-saffron/5'
                                                : 'bg-primary text-primary-foreground shadow-card hover:bg-primary/85'
                                        )}
                                    >
                                        <Bi hi='दूसरे खेल देखें' en='See the other games' layout='inline' always />
                                    </Link>
                                    <Link href='/learn' className='inline-flex min-h-tap items-center justify-center gap-2 rounded-full border border-border bg-card px-6 py-2.5 font-semibold hover:border-saffron/60 hover:bg-saffron/5'>
                                        <Bi hi='पाठ पढ़ें' en='Go to the lessons' layout='inline' always />
                                    </Link>
                                </div>
                            </div>
                        )}

                        {phase === 'done' && (
                            <SessionSummary
                                mode={mode}
                                level={level}
                                played={played}
                                bestStreak={best}
                                kids={kids}
                                signedIn={signedIn}
                                onReplay={restart}
                                topics={topics}
                                vikalp={vikalp}
                                set={place}
                            />
                        )}

                        {question && (phase === 'ready' || phase === 'checking' || phase === 'answered') && (
                            <div
                                role='group'
                                aria-label={`प्रश्न ${qIndex} / ${length} · Question ${qIndex} of ${length}`}
                                className='flex flex-col gap-5'
                            >
                                <div className='flex flex-wrap items-center gap-2'>
                                    <span className='inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-semibold tabular-nums'>
                                        <span lang='hi' className='hindi'>प्रश्न {qIndex} / {length}</span>
                                    </span>
                                    {question.set && (
                                        <span className='inline-flex items-center gap-1.5 rounded-full bg-saffron/12 px-3 py-1 text-xs font-semibold tabular-nums text-saffron-strong'>
                                            <span lang='hi' className='hindi'>सेट {question.set.set} / {question.set.sets}</span>
                                            <span aria-hidden='true' className='text-muted-foreground/70'>·</span>
                                            <span lang='en'>Set {question.set.set} of {question.set.sets}</span>
                                        </span>
                                    )}
                                    {question.mixed && (
                                        <span className='inline-flex items-center gap-1.5 rounded-full bg-saffron/12 px-3 py-1 text-xs font-semibold'>
                                            <span lang='hi' className='hindi'>{modeInfo(question.mode).name.hi}</span>
                                            <span aria-hidden='true' className='text-muted-foreground/70'>·</span>
                                            <span lang='en' className='text-muted-foreground'>{modeInfo(question.mode).name.en}</span>
                                        </span>
                                    )}
                                    {question.timed && (
                                        <span className='inline-flex items-center gap-1 rounded-full bg-terracotta/10 px-3 py-1 text-xs font-semibold text-terracotta'>
                                            <Timer className='size-3.5' aria-hidden='true' />
                                            <span lang='hi' className='hindi'>{question.seconds} सेकंड</span>
                                            <span aria-hidden='true'>·</span>
                                            <span lang='en'>{question.seconds} s</span>
                                        </span>
                                    )}
                                </div>

                                {answered === 0 && !settled && focusMissed && (
                                    <p className='rounded-xl border border-saffron/40 bg-saffron/8 px-4 py-3 text-sm'>
                                        <Bi
                                            hi={`"${focus}" के रूप अभी अभ्यास के लिए उपलब्ध नहीं हैं, इसलिए दूसरे शब्दों से अभ्यास हो रहा है।`}
                                            en={`The forms of "${focus}" are not available for practice yet, so this session uses other words.`}
                                        />
                                    </p>
                                )}

                                {answered === 0 && !settled && mode === 'aaj' && nothingDue && (
                                    <p className='rounded-xl bg-surface-muted px-4 py-3 text-sm'>
                                        <Bi
                                            hi='अभी दोहराव के लिए कुछ बाक़ी नहीं — यह अलग-अलग खेलों का मिला-जुला अभ्यास है।'
                                            en='Nothing is due for review right now; this is a mix of games to keep you sharp.'
                                        />
                                    </p>
                                )}

                                <StepPrompt text={question.prompt} />

                                {question.kind === 'choice' && question.audio && (question.audio.normal || question.audio.slow) && (
                                    <ListenControls key={question.token} audio={question.audio} kids={kids} />
                                )}

                                {question.kind === 'choice' && question.audio?.say && !question.audio.normal && !question.audio.slow && (
                                    <VoiceListenControls key={question.token} say={question.audio.say} kids={kids} />
                                )}

                                {question.cue && <QuestionCueView cue={question.cue} settled={settled} kids={kids} />}

                                {renderQuestion(question)}

                                {result && (
                                    <aside className='animate-pop-in flex flex-col gap-2 rounded-xl border border-border bg-card px-4 py-3 shadow-sm'>
                                        {!result.correct && result.expected && question.kind !== 'table' && question.kind !== 'order' && (
                                            <div>
                                                <p className='text-sm font-semibold'>
                                                    <Bi hi='सही उत्तर' en='The answer' layout='inline' always />
                                                </p>
                                                <p lang={isPrakritText(result.expected) ? 'pra' : undefined} className={isPrakritText(result.expected) ? 'pra pra-lg' : 'text-lg font-medium'}>
                                                    {result.expected}
                                                </p>
                                            </div>
                                        )}
                                        {result.vikalp && result.vikalp.length > 0 && (
                                            <div className='flex flex-col gap-1'>
                                                <p className='flex flex-wrap items-center gap-2 text-sm font-semibold'>
                                                    <StatusBadge kind='alt' />
                                                    <Bi hi='उत्तर में विकल्प रूप' en='A variant form in the answer' layout='inline' always />
                                                </p>
                                                <ul className='flex flex-col gap-0.5 text-sm'>
                                                    {result.vikalp.map(v => (
                                                        <li key={v.form} className='flex flex-wrap items-baseline gap-x-2'>
                                                            <span lang='pra' className='pra pra-md'>{v.form}</span>
                                                            <span className='text-muted-foreground'>
                                                                <span lang='hi' className='hindi'>सामान्य रूप</span>
                                                                <span aria-hidden='true'> · </span>
                                                                <span lang='en'>standard form</span>
                                                                <span aria-hidden='true'>:</span>
                                                            </span>
                                                            <span lang='pra' className='pra pra-md'>{v.core}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {result.expectedMeaning && (
                                            <p className='text-sm'>
                                                <span className='font-semibold'>
                                                    <Bi hi='अर्थ' en='Meaning' layout='inline' always />
                                                </span>
                                                <span aria-hidden='true'>: </span>
                                                <Bi hi={result.expectedMeaning.hi} en={result.expectedMeaning.en} layout='inline' enClassName='text-muted-foreground' />
                                            </p>
                                        )}
                                        <p className='text-sm font-semibold'>
                                            {result.correct ? (
                                                kids ? (
                                                    <span className='inline-flex items-center gap-1 text-saffron-strong'>
                                                        <Star className='size-4 fill-saffron' aria-hidden='true' />
                                                        <Bi hi='एक सितारा मिला!' en='You got a star!' layout='inline' always />
                                                    </span>
                                                ) : (
                                                    <span className='inline-flex items-center gap-1 text-saffron-strong'>
                                                        <Sparkles className='size-4' aria-hidden='true' />
                                                        {result.xpAwarded > 0
                                                            ? <Bi hi={`+${result.xpAwarded} अंक`} en={`+${result.xpAwarded} XP`} layout='inline' always />
                                                            : <Bi hi='इस प्रश्न के अंक पहले मिल चुके हैं' en='XP for this question was already counted' layout='inline' always />}
                                                    </span>
                                                )
                                            ) : (
                                                <span className='text-muted-foreground'>
                                                    <Bi hi='यह दोहराव सूची में जुड़ गया है।' en='This goes into your reviews.' layout='inline' always />
                                                </span>
                                            )}
                                        </p>
                                    </aside>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {primary && (
                    <footer
                        className={cn(
                            'border-t transition-colors duration-200',
                            tone === 'correct' && 'border-success/30 bg-success-soft',
                            tone === 'wrong' && 'border-danger/30 bg-danger-soft',
                            tone === 'error' && 'border-danger/30 bg-danger-soft/70',
                            !tone && 'border-border bg-background/95 backdrop-blur'
                        )}
                    >
                        <div className='mx-auto flex w-full max-w-3xl flex-col gap-3 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:flex-row sm:items-center sm:gap-6 sm:pb-4 sm:pt-4'>
                            <div role='status' aria-live='polite' className='max-h-[38vh] min-w-0 flex-1 overflow-y-auto sm:max-h-[30vh]'>
                                {checkError === 'expired' ? (
                                    <p className='flex items-start gap-3 text-sm sm:text-base'>
                                        <CircleAlert className='mt-0.5 size-6 shrink-0 text-danger' aria-hidden='true' />
                                        <Bi hi='यह प्रश्न पुराना हो गया है। एक नया प्रश्न लीजिए।' en='This question has expired. Take a new one.' />
                                    </p>
                                ) : (
                                    <FeedbackPanel
                                        result={result ? asGrade(result) : null}
                                        error={checkError === 'network'}
                                        stepType={question?.kind === 'build' ? 'build' : 'mcq'}
                                        index={answered}
                                        kids={kids}
                                        isTest
                                    />
                                )}
                                {answerForReaders && (
                                    <p className='sr-only'>
                                        <span lang='hi'>सही उत्तर</span>
                                        <span> · </span>
                                        <span lang='en'>The answer: </span>
                                        <span lang={isPrakritText(answerForReaders) ? 'pra' : undefined}>{answerForReaders}</span>
                                    </p>
                                )}
                                {!result && !checkError && question && phase === 'ready' && (
                                    <p className='hidden text-sm text-muted-foreground sm:block'>
                                        <Bi hi={HINT[question.kind].hi} en={HINT[question.kind].en} layout='inline' />
                                    </p>
                                )}
                            </div>

                            <button
                                type='button'
                                onClick={primary.action ?? undefined}
                                disabled={!primary.action}
                                aria-keyshortcuts='Enter'
                                className={cn(
                                    'inline-flex min-h-tap w-full shrink-0 items-center justify-center gap-2 rounded-full px-7 py-2.5 text-base font-semibold shadow-card transition-colors sm:w-auto sm:min-w-44',
                                    'disabled:cursor-not-allowed disabled:opacity-50',
                                    primary.tone === 'success' && 'bg-success text-white hover:bg-success/90 dark:text-background',
                                    primary.tone === 'default' && 'bg-primary text-primary-foreground hover:bg-primary/85'
                                )}
                            >
                                {primary.busy && <LoaderCircle className='size-5 animate-spin' aria-hidden='true' />}
                                <span lang='hi' className='hindi'>{primary.label.hi}</span>
                                <span aria-hidden='true' className='opacity-70'>·</span>
                                <span lang='en'>{primary.label.en}</span>
                                {phase === 'answered' && <ArrowRight className='size-5' aria-hidden='true' />}
                            </button>
                        </div>
                    </footer>
                )}
            </div>
        </MotionConfig>
    )
}

/** The page heading under the session overlay (read by screen readers and search engines). */
export const SessionHeading: FC<{ mode: SessionMode, level: Level }> = ({ mode, level }): ReactNode => {
    const name = sessionName(mode)
    const line = mode === 'aaj' ? MIXED_INFO.line : modeInfo(mode).line

    return (
        <div className='sr-only'>
            <h1>
                <span lang='hi'>{name.hi}</span> · <span lang='en'>{name.en}</span> · <span lang='hi'>{LEVEL_INFO[level].name.hi}</span>
            </h1>
            <p>
                <span lang='hi'>{line.hi}</span> <span lang='en'>{line.en}</span>
            </p>
        </div>
    )
}
